import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { AlertCircle, ChevronDown, Loader2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  DespachoSectionCard,
  DespachoSimpleTable,
} from "@/components/despacho/DespachoUi";
import {
  FILING_VIEW_TABS,
  filingTypeLabel,
  instanceMatchesFilingView,
  taskMatchesFilingView,
} from "@/modules/despacho/complianceInstanceHelpers";
import {
  buildFiscalClientAuxMap,
  buildFiscalPendingSections,
  groupFiscalPendingByCompany,
} from "@/modules/despacho/despachoFiscalBoardHelpers";
import ComplianceInstanceDetailDialog from "@/pages/despacho/ComplianceInstanceDetailDialog";
import FiscalTrackingPanel from "@/pages/despacho/FiscalTrackingPanel";
import { syncWorkClientFromDespacho } from "@/lib/clientSelection";
import {
  TAREAS_FISCALES_VIEW_PARAM,
  TRACKING_CLIENT_PARAM,
  TRACKING_OPEN_MONTH_PARAM,
  TRACKING_PERIOD_PARAM,
} from "@/lib/taxSummaryNavigation";

const VIEW_MIS = "mis";
const VIEW_EMPRESA = "empresa";
const VIEW_AUX = "auxiliar";
const VIEW_TRACKING = "seguimiento_anual";
const VIEW_DECLARED = "declared";
const VIEW_COMP = "complementaria_pending";

const TABS_NEEDING_FISCAL_TASKS = new Set([
  VIEW_MIS,
  VIEW_EMPRESA,
  VIEW_AUX,
  VIEW_DECLARED,
  VIEW_COMP,
]);

function buildFiscalTaskColumns(baseColumns) {
  const extra = [
    {
      key: "fecha_operativa",
      label: "F. operativa",
      render: (r) => (r.fecha_operativa ? String(r.fecha_operativa).slice(0, 10) : "—"),
    },
    {
      key: "fecha_fiscal",
      label: "F. fiscal",
      render: (r) => (r.fecha_fiscal ? String(r.fecha_fiscal).slice(0, 10) : r.due_date ? String(r.due_date).slice(0, 10) : "—"),
    },
    {
      key: "filing_type_label",
      label: "Declaración",
      render: (r) => r.filing_type_label || filingTypeLabel(r.filing_type) || "—",
    },
    {
      key: "tax_payment_label",
      label: "Impuesto",
      render: (r) => r.tax_payment_label || (r.tax_paid ? "Pagado" : "—"),
    },
  ];
  const dueIdx = baseColumns.findIndex((c) => c.key === "due_date");
  if (dueIdx < 0) return [...extra, ...baseColumns];
  return [
    ...baseColumns.slice(0, dueIdx + 1),
    ...extra,
    ...baseColumns.slice(dueIdx + 1),
  ];
}

function PendingAuxSectionCollapsible({
  section,
  companyGroups,
  fiscalColumns,
  instanceColumns,
  panelKey,
}) {
  const [open, setOpen] = useState(false);

  const description = section.clients_assigned
    ? `${section.clients_assigned} empresa(s) · ${section.tasks.length} tarea(s) · ${section.instances.length} vencimiento(s)`
    : `${section.tasks.length} tarea(s) · ${section.instances.length} vencimiento(s)`;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-sm border border-slate-200 bg-white p-4 space-y-3"
      data-testid={`despacho-fiscales-pending-aux-${section.assigned_to_user_id || section.auxiliar_name}`}
    >
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 text-left hover:bg-slate-50/80 -m-1 p-1 rounded-sm">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-bold text-[#0F2D5C] m-0">{section.auxiliar_name}</h2>
          <p className="text-xs text-slate-500 mt-1 m-0">{description}</p>
        </div>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-2 pt-1 data-[state=closed]:hidden">
        {companyGroups.length ? (
          companyGroups.map((company) => (
            <PendingCompanyCollapsible
              key={`${panelKey}-${company.id}`}
              company={company}
              fiscalColumns={fiscalColumns}
              instanceColumns={instanceColumns}
            />
          ))
        ) : (
          <p className="text-sm text-slate-600 m-0">
            Sin tareas ni vencimientos en este periodo. Al entrar se sincronizan vencimientos automáticamente;
            revise obligaciones activas en Clientes → Obligaciones y reglas en Calendario si sigue vacío.
          </p>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
}

function PendingCompanyCollapsible({
  company,
  fiscalColumns,
  instanceColumns,
}) {
  const [open, setOpen] = useState(false);

  const taskCount = company.tasks?.length || 0;
  const instanceCount = company.instances?.length || 0;
  const summary = [
    taskCount ? `${taskCount} tarea(s)` : null,
    instanceCount ? `${instanceCount} vencimiento(s)` : null,
  ].filter(Boolean).join(" · ") || "Sin vencimientos en el periodo";

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-sm border border-slate-200 bg-white"
      data-testid={`despacho-fiscales-pending-company-${company.id}`}
    >
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left hover:bg-slate-50/80">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-slate-900 m-0 truncate">
            {company.name}
            {company.rfc ? (
              <span className="text-xs font-normal text-slate-500 ml-1.5">{company.rfc}</span>
            ) : null}
          </p>
          <p className="text-[10px] text-slate-500 m-0 mt-0.5">{summary}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {company.suspended ? (
            <Badge variant="secondary" className="rounded-sm text-[10px] bg-amber-50 text-amber-950 border-amber-200">
              Suspendida
            </Badge>
          ) : null}
          <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="px-3 pb-3 space-y-3 border-t border-slate-100 data-[state=closed]:hidden">
        {company.tasks?.length ? (
          <DespachoSimpleTable columns={fiscalColumns} rows={company.tasks} emptyLabel="Sin tareas." />
        ) : null}
        {company.instances?.length ? (
          <div className={company.tasks?.length ? "pt-1" : ""}>
            {company.tasks?.length ? (
              <p className="text-[11px] font-semibold text-[#0F2D5C] m-0 mb-2">Vencimientos del periodo</p>
            ) : null}
            <DespachoSimpleTable columns={instanceColumns} rows={company.instances} emptyLabel="Sin vencimientos." />
          </div>
        ) : null}
        {!company.tasks?.length && !company.instances?.length ? (
          <p className="text-xs text-slate-500 m-0 pt-2">
            Sin tareas ni vencimientos en este periodo. Revise obligaciones activas en Clientes → Obligaciones
            o genere vencimientos desde Seguimiento de tareas.
          </p>
        ) : null}
      </CollapsibleContent>
    </Collapsible>
  );
}

function EmpresaSectionCollapsible({
  section,
  sectionTasks,
  sectionInstances,
  fiscalColumns,
  instanceColumns,
}) {
  const [open, setOpen] = useState(false);

  const description = section.suspended
    ? `Suspendida · Auxiliar: ${section.assignedLabel}`
    : `Auxiliar: ${section.assignedLabel} · ${sectionTasks.length} tarea(s) · ${sectionInstances.length} vencimiento(s)`;

  return (
    <Collapsible
      open={open}
      onOpenChange={setOpen}
      className="rounded-sm border border-slate-200 bg-white p-4 space-y-3"
      data-testid={`despacho-fiscales-empresa-${section.id}`}
    >
      <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 text-left hover:bg-slate-50/80 -m-1 p-1 rounded-sm">
        <div className="min-w-0 flex-1">
          <h2 className="text-sm font-bold text-[#0F2D5C] m-0">{section.clientName}</h2>
          <p className="text-xs text-slate-500 mt-1 m-0">{description}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {section.suspended ? (
            <Badge variant="secondary" className="rounded-sm text-[10px] bg-amber-50 text-amber-950 border-amber-200">
              Suspendida
            </Badge>
          ) : null}
          <ChevronDown className={`h-4 w-4 text-slate-500 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 pt-1 data-[state=closed]:hidden">
        {section.rfc ? (
          <p className="text-[11px] text-slate-500 m-0">RFC: {section.rfc}</p>
        ) : null}
        {sectionTasks.length ? (
          <DespachoSimpleTable columns={fiscalColumns} rows={sectionTasks} emptyLabel="Sin tareas." />
        ) : null}
        {sectionInstances.length ? (
          <div className={sectionTasks.length ? "mt-1" : ""}>
            {sectionTasks.length ? (
              <p className="text-[11px] font-semibold text-[#0F2D5C] m-0 mb-2">Vencimientos</p>
            ) : null}
            <DespachoSimpleTable columns={instanceColumns} rows={sectionInstances} emptyLabel="Sin vencimientos." />
          </div>
        ) : null}
        {!sectionTasks.length && !sectionInstances.length ? (
          <p className="text-sm text-slate-600 m-0">
            Sin tareas ni vencimientos en el periodo. Los vencimientos se generan al abrir Tareas fiscales o Seguimiento de tareas.
          </p>
        ) : null}
      </CollapsibleContent>
    </Collapsible>
  );
}

function buildInstanceColumns(onOpen) {
  return [
    { key: "client_name", label: "Cliente", render: (r) => r.client_name || r.client_rfc || "—" },
    { key: "obligation_name", label: "Obligación", render: (r) => r.obligation_name || "—" },
    { key: "period", label: "Periodo", render: (r) => r.period || r.period_key || "—" },
    {
      key: "filing_type_label",
      label: "Tipo",
      render: (r) => r.filing_type_label || filingTypeLabel(r.filing_type),
    },
    {
      key: "declared_at",
      label: "F. declaración",
      render: (r) => (r.declared_at ? String(r.declared_at).slice(0, 10) : "—"),
    },
    {
      key: "tax_payment_label",
      label: "Impuesto",
      render: (r) => r.tax_payment_label || (r.tax_paid ? "Pagado" : "Pendiente"),
    },
    {
      key: "client_reminder_label",
      label: "Recordatorio",
      render: (r) => r.client_reminder_label || "—",
    },
    {
      key: "_open",
      label: "",
      render: (r) => (
        <Button type="button" variant="link" size="sm" className="h-7 px-0 text-xs" onClick={() => onOpen(r)}>
          Detalle
        </Button>
      ),
    },
  ];
}

export default function TareasFiscalesPanel({
  readOnly = false,
  periodLabel,
  dateFrom = "",
  dateTo = "",
  tasks = [],
  complianceInstances = [],
  clientGroups = [],
  companySections = [],
  auxSummary = [],
  boardItems = [],
  loadState,
  loadMessage,
  columns,
  onInstancesChanged,
  backgroundSyncing = false,
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const qView = String(searchParams.get(TAREAS_FISCALES_VIEW_PARAM) || "").trim();
  const initialView = FILING_VIEW_TABS.some((tab) => tab.value === qView) ? qView : VIEW_TRACKING;
  const [view, setView] = useState(initialView);
  const [misPanelKey, setMisPanelKey] = useState(0);
  const [empresaPanelKey, setEmpresaPanelKey] = useState(0);
  const prevViewRef = useRef(view);
  const [instanceDetailId, setInstanceDetailId] = useState(null);
  const [instanceDetailOpen, setInstanceDetailOpen] = useState(false);
  const fiscalColumns = useMemo(() => buildFiscalTaskColumns(columns), [columns]);

  const trackingDeepLink = useMemo(() => ({
    clientId: String(searchParams.get(TRACKING_CLIENT_PARAM) || "").trim(),
    periodKey: String(searchParams.get(TRACKING_PERIOD_PARAM) || "").trim(),
    openMonth: searchParams.get(TRACKING_OPEN_MONTH_PARAM) === "1",
  }), [searchParams]);

  const clearTrackingDeepLink = () => {
    const next = new URLSearchParams(searchParams);
    next.delete(TRACKING_CLIENT_PARAM);
    next.delete(TRACKING_PERIOD_PARAM);
    next.delete(TRACKING_OPEN_MONTH_PARAM);
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    if (qView && FILING_VIEW_TABS.some((tab) => tab.value === qView) && qView !== view) {
      setView(qView);
    }
  }, [qView, view]);

  const filteredTasks = useMemo(
    () => (tasks || []).filter((t) => taskMatchesFilingView(t, view)),
    [tasks, view],
  );

  const filteredInstances = useMemo(
    () => (complianceInstances || []).filter((i) => instanceMatchesFilingView(i, view)),
    [complianceInstances, view],
  );

  const instanceColumns = useMemo(
    () => buildInstanceColumns((row) => {
      syncWorkClientFromDespacho(row.fiscal_client_id);
      setInstanceDetailId(row.id);
      setInstanceDetailOpen(true);
    }),
    [],
  );

  const auxSummaryRows = useMemo(
    () => (auxSummary || []).map((row) => ({
      ...row,
      id: row.assigned_to_user_id || row.auxiliar_name,
    })),
    [auxSummary],
  );

  const auxColumns = useMemo(
    () => [
      { key: "auxiliar_name", label: "Auxiliar", render: (r) => r.auxiliar_name || "—" },
      {
        key: "clients_assigned",
        label: "Empresas asignadas",
        render: (r) => (
          <div className="min-w-0">
            <span className="text-sm font-medium text-slate-900">{r.clients_assigned ?? 0}</span>
            {r.assigned_companies_label && r.assigned_companies_label !== "—" ? (
              <p className="text-[10px] text-slate-500 m-0 mt-0.5 line-clamp-2">{r.assigned_companies_label}</p>
            ) : (
              <p className="text-[10px] text-slate-400 m-0 mt-0.5">Sin empresas en asignación</p>
            )}
          </div>
        ),
      },
      { key: "pending", label: "Pendientes", render: (r) => r.pending ?? 0 },
      { key: "in_progress", label: "En proceso", render: (r) => r.in_progress ?? 0 },
      { key: "overdue", label: "Vencidas", render: (r) => r.overdue ?? 0 },
      { key: "done", label: "Terminadas", render: (r) => r.done ?? 0 },
    ],
    [],
  );

  const auxWithCompanies = useMemo(
    () => auxSummaryRows.filter((r) => (r.assigned_companies || []).length > 0),
    [auxSummaryRows],
  );

  const clientToAuxMap = useMemo(
    () => buildFiscalClientAuxMap(boardItems),
    [boardItems],
  );

  const pendingSections = useMemo(
    () => buildFiscalPendingSections({
      auxSummary: auxSummaryRows,
      tasks: filteredTasks,
      complianceInstances,
      clientToAuxMap,
    }),
    [auxSummaryRows, filteredTasks, complianceInstances, clientToAuxMap],
  );

  const openTaskCount = useMemo(
    () => (filteredTasks || []).filter((t) => {
      const st = String(t.status || "").toLowerCase();
      return !["done", "completada", "cancelled", "cancelada", "not_applicable_this_period", "no_aplico_este_mes"].includes(st);
    }).length,
    [filteredTasks],
  );

  useEffect(() => {
    if (view === VIEW_MIS && prevViewRef.current !== VIEW_MIS) {
      setMisPanelKey((key) => key + 1);
    }
    if (view === VIEW_EMPRESA && prevViewRef.current !== VIEW_EMPRESA) {
      setEmpresaPanelKey((key) => key + 1);
    }
    prevViewRef.current = view;
  }, [view]);

  const instancesByClient = useMemo(() => {
    const map = new Map();
    for (const inst of complianceInstances || []) {
      const cid = inst?.fiscal_client_id;
      if (!cid) continue;
      if (!map.has(cid)) map.set(cid, []);
      map.get(cid).push(inst);
    }
    return map;
  }, [complianceInstances]);

  const filingViewTabs = FILING_VIEW_TABS;

  return (
    <div className="space-y-4" data-testid="despacho-tareas-fiscales-panel">
      <Tabs value={view} onValueChange={setView} className="w-full">
        <TabsList className="flex flex-wrap h-auto gap-1 mb-2 max-w-full" data-testid="despacho-fiscales-view-tabs">
          {filingViewTabs.map((tab) => (
            <TabsTrigger key={tab.value} value={tab.value} className="text-xs rounded-sm">
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {loadState === "loading" && TABS_NEEDING_FISCAL_TASKS.has(view) ? (
          <div className="flex items-center gap-2 text-sm text-slate-600 py-6">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            Cargando tareas…
          </div>
        ) : null}

        {loadState === "ready" && backgroundSyncing && TABS_NEEDING_FISCAL_TASKS.has(view) ? (
          <div
            className="flex items-center gap-2 text-xs text-slate-600 py-2 px-3 rounded-sm border border-slate-200 bg-slate-50 mb-2"
            data-testid="despacho-fiscales-background-sync"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" aria-hidden />
            Sincronizando vencimientos del mes en segundo plano…
          </div>
        ) : null}

        {loadState === "error" || loadState === "forbidden" ? (
          TABS_NEEDING_FISCAL_TASKS.has(view) ? (
          <div className="rounded-sm border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-950 flex items-start gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" aria-hidden />
            <p className="m-0">{loadMessage}</p>
          </div>
          ) : null
        ) : null}

        {[VIEW_DECLARED, VIEW_COMP].map((viewKey) => (
          <TabsContent key={viewKey} value={viewKey} className="mt-0">
            {loadState === "ready" && view === viewKey ? (
              <DespachoSectionCard
                title={filingViewTabs.find((t) => t.value === viewKey)?.label || "Seguimiento"}
                description={`${filteredInstances.length} vencimiento(s) · ${periodLabel}`}
              >
                {filteredInstances.length ? (
                  <DespachoSimpleTable
                    columns={instanceColumns}
                    rows={filteredInstances}
                    emptyLabel="Sin registros."
                  />
                ) : (
                  <p className="text-sm text-slate-600 m-0">
                    No hay vencimientos en esta categoría para el periodo. Registre la declaración en el detalle del vencimiento.
                  </p>
                )}
              </DespachoSectionCard>
            ) : null}
          </TabsContent>
        ))}

        <TabsContent value={VIEW_TRACKING} className="mt-0">
          {view === VIEW_TRACKING ? (
            <FiscalTrackingPanel
              readOnly={readOnly}
              onInstancesChanged={onInstancesChanged}
              trackingDeepLink={trackingDeepLink}
              onTrackingDeepLinkConsumed={clearTrackingDeepLink}
            />
          ) : null}
        </TabsContent>

        <TabsContent value={VIEW_MIS} className="mt-0">
          {loadState === "ready" && view === VIEW_MIS ? (
            pendingSections.length ? (
              <div className="space-y-4" data-testid="despacho-fiscales-pendientes-aux" key={misPanelKey}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-xs text-slate-600 m-0">
                    {openTaskCount} tarea(s) abierta(s) · {pendingSections.length} auxiliar(es) con cartera o vencimientos
                    {boardItems.length ? ` · ${boardItems.length} empresa(s) en catálogo` : ""}.
                  </p>
                </div>
                {pendingSections.map((section) => {
                  const companyGroups = groupFiscalPendingByCompany(section);
                  return (
                    <PendingAuxSectionCollapsible
                      key={section.assigned_to_user_id || section.auxiliar_name}
                      section={section}
                      companyGroups={companyGroups}
                      fiscalColumns={fiscalColumns}
                      instanceColumns={instanceColumns}
                      panelKey={view}
                    />
                  );
                })}
              </div>
            ) : (
              <DespachoSectionCard
                title={`Pendientes del mes — ${periodLabel}`}
                description={
                  boardItems.length
                    ? `${boardItems.length} empresa(s) en catálogo · sin auxiliares con asignación`
                    : "0 auxiliares con empresas o tareas."
                }
              >
                <p className="text-sm text-slate-600 m-0 mb-3">
                  {boardItems.length
                    ? "Hay empresas fiscales registradas pero ninguna está asignada a un auxiliar. Use Clientes → Asignación para vincularlas."
                    : "No hay auxiliares con empresas ni tareas fiscales. Asigne empresas en Clientes; al entrar se sincronizan vencimientos y tareas del periodo."}
                </p>
              </DespachoSectionCard>
            )
          ) : null}
        </TabsContent>

        <TabsContent value={VIEW_EMPRESA} className="mt-0">
          {loadState === "ready" && view === VIEW_EMPRESA ? (
            companySections.length ? (
              <div className="space-y-4" data-testid="despacho-fiscales-por-empresa" key={empresaPanelKey}>
                <p className="text-xs text-slate-600 m-0">
                  {companySections.length} empresa(s) en el catálogo fiscal · {filteredTasks.length} tarea(s) en el periodo.
                </p>
                {companySections.map((section) => {
                  const sectionTasks = (section.tasks || []).filter((t) => taskMatchesFilingView(t, VIEW_EMPRESA));
                  const sectionInstances = instancesByClient.get(section.id) || [];
                  return (
                    <EmpresaSectionCollapsible
                      key={section.id}
                      section={section}
                      sectionTasks={sectionTasks}
                      sectionInstances={sectionInstances}
                      fiscalColumns={fiscalColumns}
                      instanceColumns={instanceColumns}
                    />
                  );
                })}
              </div>
            ) : (
              <DespachoSectionCard title={`Empresas — ${periodLabel}`}>
                <p className="text-sm text-slate-600 m-0">
                  No hay empresas en el catálogo fiscal. Regístrelas en Clientes y asigne auxiliares.
                </p>
              </DespachoSectionCard>
            )
          ) : null}
        </TabsContent>

        <TabsContent value={VIEW_AUX} className="mt-0">
          {loadState === "ready" && view === VIEW_AUX ? (
          <DespachoSectionCard
            title="Resumen por auxiliar"
            description="Empresas asignadas en Clientes y conteo de tareas fiscales del periodo filtrado."
          >
            {auxSummaryRows.length ? (
              <>
                <DespachoSimpleTable
                  columns={auxColumns}
                  rows={auxSummaryRows}
                  emptyLabel="Sin auxiliares registrados."
                />
                {auxWithCompanies.length ? (
                  <div className="mt-4 space-y-3" data-testid="despacho-fiscales-aux-companies">
                    <p className="text-xs font-semibold text-[#0F2D5C] m-0">Detalle de empresas por auxiliar</p>
                    {auxWithCompanies.map((row) => (
                      <div
                        key={row.id}
                        className="rounded-sm border border-slate-200 bg-slate-50/60 px-3 py-2"
                        data-testid={`despacho-fiscales-aux-companies-${row.assigned_to_user_id}`}
                      >
                        <p className="text-sm font-medium text-slate-900 m-0 mb-1.5">
                          {row.auxiliar_name}
                          <span className="text-xs font-normal text-slate-500 ml-2">
                            ({row.clients_assigned} empresa{row.clients_assigned === 1 ? "" : "s"})
                          </span>
                        </p>
                        <div className="flex flex-wrap gap-1.5">
                          {(row.assigned_companies || []).map((company) => (
                            <Badge
                              key={company.id}
                              variant="secondary"
                              className={`rounded-sm text-[10px] font-normal ${company.suspended ? "bg-amber-50 text-amber-950 border-amber-200" : ""}`}
                            >
                              {company.name}
                              {company.rfc ? ` · ${company.rfc}` : ""}
                              {company.suspended ? " (suspendida)" : ""}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 m-0 mt-3">
                    Ningún auxiliar tiene empresas asignadas. Use Clientes → Asignación para vincularlas.
                  </p>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-600 m-0">
                No hay auxiliares en el equipo. Dé de alta usuarios en Equipo o asigne empresas en Clientes.
              </p>
            )}
          </DespachoSectionCard>
          ) : null}
        </TabsContent>
      </Tabs>

      <ComplianceInstanceDetailDialog
        instanceId={instanceDetailId}
        open={instanceDetailOpen}
        onOpenChange={setInstanceDetailOpen}
        readOnly={readOnly}
        onUpdated={() => onInstancesChanged?.()}
      />
    </div>
  );
}
